/**
 * Org salary structure method helpers (CTC | GROSS | BOTH).
 */
export const SALARY_STRUCTURE_METHOD_CHANGED_EVENT =
  "pacehrm:salary-structure-method-changed";

export function normalizeSalaryStructureMethod(value) {
  const v = String(value || "CTC").toUpperCase();
  if (v === "GROSS" || v === "BOTH") return v;
  return "CTC";
}

/** Profile field label for Org Details CTC amount. */
export function salaryAmountFieldLabel(method, profileMode = null) {
  const m = normalizeSalaryStructureMethod(method);
  if (m === "BOTH") {
    const mode = String(profileMode || "CTC").toUpperCase() === "GROSS" ? "GROSS" : "CTC";
    return mode === "GROSS" ? "Gross per Annum" : "CTC per annum";
  }
  return m === "GROSS" ? "Gross per Annum" : "CTC per annum";
}

/** Effective Mode for designer: locked when CTC/GROSS only. */
export function resolveStructureMode(method, selectedMode = "CTC") {
  const m = normalizeSalaryStructureMethod(method);
  if (m === "BOTH") {
    return String(selectedMode || "CTC").toUpperCase() === "GROSS" ? "GROSS" : "CTC";
  }
  return m;
}

export function canChooseStructureMode(method) {
  return normalizeSalaryStructureMethod(method) === "BOTH";
}

/** True when structure / salary type is Gross (not CTC). */
export function isGrossSalaryMode(modeOrSalaryType) {
  const v = String(modeOrSalaryType || "").trim().toUpperCase();
  return v === "GROSS";
}

/**
 * Profile package match for structure preview / save / validate.
 *
 * GROSS: Profile Gross = Subtotal (E) only (employer benefits B not in match).
 * CTC:   Profile CTC   = Total CTC (E + B, and R when reimbursements apply).
 *
 * @param {object} opts
 * @param {string} opts.mode - "CTC" | "GROSS" (or salaryType "ctc"|"gross")
 * @param {number} opts.profileAmount - annual or monthly profile amount (same basis as matched)
 * @param {number} opts.grossAmount - Subtotal (E) / Gross Salary (same basis)
 * @param {number} opts.totalCtcAmount - Total CTC E+B[+R] (same basis)
 * @param {boolean} [opts.hasReimbursements]
 * @param {number} [opts.tolerance=1] - rupee rounding tolerance
 */
export function matchProfilePackageAmount({
  mode,
  profileAmount,
  grossAmount,
  totalCtcAmount,
  hasReimbursements = false,
  tolerance = 1,
} = {}) {
  const isGross = isGrossSalaryMode(mode);
  const target = Math.round(Number(profileAmount) || 0);
  const matched = Math.round(
    isGross ? Number(grossAmount) || 0 : Number(totalCtcAmount) || 0
  );
  const delta = matched - target;
  const matchedLabel = isGross
    ? "Subtotal (E) / Gross Salary"
    : hasReimbursements
      ? "Total CTC (E + R + B)"
      : "Total CTC (E + B)";
  const profileLabel = isGross ? "Profile Gross" : "Profile CTC";

  const tol = Number.isFinite(Number(tolerance)) ? Number(tolerance) : 1;
  return {
    ok: Math.abs(delta) <= tol,
    isGrossMode: isGross,
    targetAmount: target,
    matchedAmount: matched,
    delta,
    matchedLabel,
    profileLabel,
    // aliases used by existing call sites (annual naming)
    targetAnnual: target,
    matchedAnnual: matched,
  };
}
